import type { Position, Size } from '../types';
import { TASKBAR_HEIGHT } from './constants';

// Generate unique ID
export const generateId = (): string => {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

// Clamp value between min and max
export const clamp = (value: number, min: number, max: number): number => {
  return Math.min(Math.max(value, min), max);
};

// Get centered position for a window
export const getCenteredPosition = (windowSize: Size): Position => {
  const screenWidth = window.innerWidth;
  const screenHeight = window.innerHeight - TASKBAR_HEIGHT;

  return {
    x: Math.max(0, (screenWidth - windowSize.width) / 2),
    y: Math.max(0, (screenHeight - windowSize.height) / 2),
  };
};

// Ensure position is within screen bounds
export const constrainPosition = (
  position: Position,
  size: Size,
  bounds?: { width: number; height: number }
): Position => {
  const maxWidth = bounds?.width ?? window.innerWidth;
  const maxHeight = bounds?.height ?? window.innerHeight - TASKBAR_HEIGHT;

  return {
    x: clamp(position.x, 0, maxWidth - size.width),
    y: clamp(position.y, 0, maxHeight - size.height),
  };
};

// Get cascaded position for new windows
let cascadeOffset = 0;
export const getCascadedPosition = (windowSize: Size): Position => {
  const offset = cascadeOffset * 30;
  cascadeOffset = (cascadeOffset + 1) % 10;

  const centered = getCenteredPosition(windowSize);
  return constrainPosition(
    { x: centered.x + offset, y: centered.y + offset },
    windowSize
  );
};

// Reset cascade offset
export const resetCascade = (): void => {
  cascadeOffset = 0;
};

// Format date for display
export const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

// Get current time for system tray
export const getCurrentTime = (): string => {
  const now = new Date();
  return now.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

// Detect if device is touch-enabled
export const isTouchDevice = (): boolean => {
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
};

// Debounce function
export const debounce = <T extends (...args: unknown[]) => unknown>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: ReturnType<typeof setTimeout> | null = null;

  return (...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

// Throttle function
export const throttle = <T extends (...args: unknown[]) => unknown>(
  func: T,
  limit: number
): ((...args: Parameters<T>) => void) => {
  let inThrottle = false;

  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
};
